import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import { TALK_THEME_IDS } from '../config/talkThemes';
import { TOPIC_PAGE_ROUTES } from '../containers/TopicPage/topicRoutes';
import { TALKER_QA_PAGES } from '../containers/TalkerQAPage/talkerQaPages';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const ChooseAccountRolePage = loadable(() => import(/* webpackChunkName: "ChooseAccountRolePage" */ '../containers/ChooseAccountRolePage/ChooseAccountRolePage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const DashboardPage = loadable(() => import(/* webpackChunkName: "DashboardPage" */ '../containers/DashboardPage/DashboardPage'));
const BrowsePage = loadable(() => import(/* webpackChunkName: "BrowsePage" */ '../containers/BrowsePage/BrowsePage'));
const MyProfileHubPage = loadable(() => import(/* webpackChunkName: "MyProfileHubPage" */ '../containers/MyProfileHubPage/MyProfileHubPage'));
const FavoritesPage = loadable(() => import(/* webpackChunkName: "FavoritesPage" */ '../containers/FavoritesPage/FavoritesPage'));
const PastSessionsPage = loadable(() => import(/* webpackChunkName: "PastSessionsPage" */ '../containers/PastSessionsPage/PastSessionsPage'));
const NotificationsPage = loadable(() => import(/* webpackChunkName: "NotificationsPage" */ '../containers/NotificationsPage/NotificationsPage'));
const SessionsCalendarPage = loadable(() => import(/* webpackChunkName: "SessionsCalendarPage" */ '../containers/SessionsCalendarPage/SessionsCalendarPage'));
const ListenerEarningsPage = loadable(() => import(/* webpackChunkName: "ListenerEarningsPage" */ '../containers/ListenerEarningsPage/ListenerEarningsPage'));
const AvailabilityPage = loadable(() => import(/* webpackChunkName: "AvailabilityPage" */ '../containers/AvailabilityPage/AvailabilityPage'));
const OnboardingPage = loadable(() => import(/* webpackChunkName: "OnboardingPage" */ '../containers/OnboardingPage/OnboardingPage'));
const IdentityReturnPage = loadable(() => import(/* webpackChunkName: "IdentityReturnPage" */ '../containers/IdentityReturnPage/IdentityReturnPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const InboxLanding = loadable(() => import(/* webpackChunkName: "InboxLanding" */ '../containers/InboxPage/InboxLanding'));
const MakeOfferPage = loadable(() => import(/* webpackChunkName: "MakeOfferPage" */ '../containers/MakeOfferPage/MakeOfferPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const FreeBookingPage = loadable(() => import(/* webpackChunkName: "FreeBookingPage" */ '../containers/FreeBookingPage/FreeBookingPage'));
const ListingBookingPage = loadable(() => import(/* webpackChunkName: "ListingBookingPage" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingBookingPage'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const NewListingPage = loadable(() => import(/* webpackChunkName: "NewListingPage" */ '../containers/NewListingPage/NewListingPage'));
const ManageAccountPage = loadable(() => import(/* webpackChunkName: "ManageAccountPage" */ '../containers/ManageAccountPage/ManageAccountPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const PromotePage = loadable(() => import(/* webpackChunkName: "PromotePage" */ '../containers/PromotePage/PromotePage'));
const ContentPage = loadable(() => import(/* webpackChunkName: "ContentPage" */ '../containers/ContentPage/ContentPage'));
const PricingPage = loadable(() => import(/* webpackChunkName: "PricingPage" */ '../containers/PricingPage/PricingPage'));
const HowItWorksPage = loadable(() => import(/* webpackChunkName: "HowItWorksPage" */ '../containers/HowItWorksPage/HowItWorksPage'));
const WelcomePage = loadable(() => import(/* webpackChunkName: "WelcomePage" */ '../containers/WelcomePage/WelcomePage'));
const Free15Page = loadable(() => import(/* webpackChunkName: "Free15Page" */ '../containers/Free15Page/Free15Page'));
const GuestTalkPage = loadable(() => import(/* webpackChunkName: "GuestTalkPage" */ '../containers/GuestTalkPage/GuestTalkPage'));
const ForTalkersPage = loadable(() => import(/* webpackChunkName: "TalkerAudiencePage" */ '../containers/NewAudiencePage/TalkerAudiencePage'));
const TalkAndListenPage = loadable(() => import(/* webpackChunkName: "BothAudiencePage" */ '../containers/NewAudiencePage/BothAudiencePage'));
const ForListenersPage = loadable(() => import(/* webpackChunkName: "ForListenersPage" */ '../containers/ForListenersPage/ForListenersPage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ '../containers/AboutPage/AboutPage'));
const SweetTreatPage = loadable(() => import(/* webpackChunkName: "SweetTreatPage" */ '../containers/SweetTreatPage/SweetTreatPage'));
const FAQPage = loadable(() => import(/* webpackChunkName: "FAQPage" */ '../containers/FAQPage/FAQPage'));
const EarnersPlaybookPage = loadable(() => import(/* webpackChunkName: "EarnersPlaybookPage" */ '../containers/EarnersPlaybookPage/EarnersPlaybookPage'));
const TipsPage = loadable(() => import(/* webpackChunkName: "TipsPage" */ '../containers/TipsPage/TipsPage'));
const LearnPage = loadable(() => import(/* webpackChunkName: "LearnAudiencePage" */ '../containers/NewAudiencePage/LearnAudiencePage'));
const BecomeAnEarnerPage = loadable(() => import(/* webpackChunkName: "EarnerAudiencePage" */ '../containers/NewAudiencePage/EarnerAudiencePage'));
const TeachPage = loadable(() => import(/* webpackChunkName: "TeachAudiencePage" */ '../containers/NewAudiencePage/TeachAudiencePage'));
const StartAdPage = loadable(() => import(/* webpackChunkName: "StartAdPage" */ '../containers/StartAdPage/StartAdPage'));
const BecomeAListenerPage = loadable(() => import(/* webpackChunkName: "ListenerAudiencePage" */ '../containers/NewAudiencePage/ListenerAudiencePage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ '../containers/ContactPage/ContactPage'));
const GuidePage = loadable(() => import(/* webpackChunkName: "GuidePage" */ '../containers/GuidePage/GuidePage'));
const TalkAboutTopicPage = loadable(() => import(/* webpackChunkName: "TalkAboutTopicPage" */ '../containers/TalkAboutTopicPage/TalkAboutTopicPage'));
const TalkerQAPage = loadable(() => import(/* webpackChunkName: "TalkerQAPage" */ '../containers/TalkerQAPage/TalkerQAPage'));
const SweetSkillsPage = loadable(() => import(/* webpackChunkName: "SkillzHubPage" */ '../containers/SkillzHubPage/SkillzHubPage'));
const SweetSkillsBrowsePage = loadable(() => import(/* webpackChunkName: "SweetSkillsPage" */ '../containers/SweetSkillsPage/SweetSkillsPage'));
const TopicPage = loadable(() => import(/* webpackChunkName: "TopicPage" */ '../containers/TopicPage/TopicPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const RequestQuotePage = loadable(() => import(/* webpackChunkName: "RequestQuotePage" */ '../containers/RequestQuotePage/RequestQuotePage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const SafetyPage = loadable(() => import(/* webpackChunkName: "SafetyPage" */ '../containers/SafetyPage/SafetyPage'));
const SmsTermsPage = loadable(() => import(/* webpackChunkName: "SmsTermsPage" */ '../containers/SmsTermsPage/SmsTermsPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));
const NoAccessPage = loadable(() => import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));
const OpsModerationPage = loadable(() => import(/* webpackChunkName: "OpsModerationPage" */ '../containers/OpsModerationPage/OpsModerationPage'));
const SafetyReviewPage = loadable(() => import(/* webpackChunkName: "SafetyReviewPage" */ '../containers/SafetyReviewPage/SafetyReviewPage'));
const OpsFoundingPage = loadable(() => import(/* webpackChunkName: "OpsFoundingPage" */ '../containers/OpsFoundingPage/OpsFoundingPage'));
const OpsPromotionsPage = loadable(() => import(/* webpackChunkName: "OpsPromotionsPage" */ '../containers/OpsPromotionsPage/OpsPromotionsPage'));
const OpsInsightsPage = loadable(() => import(/* webpackChunkName: "OpsInsightsPage" */ '../containers/OpsInsightsPage/OpsInsightsPage'));
const OpsPaypalPayoutsPage = loadable(() => import(/* webpackChunkName: "OpsPaypalPayoutsPage" */ '../containers/OpsPaypalPayoutsPage/OpsPaypalPayoutsPage'));
const OpsHelloVideoPage = loadable(() => import(/* webpackChunkName: "OpsHelloVideoPage" */ '../containers/OpsHelloVideoPage/OpsHelloVideoPage'));
const OpsIdReviewPage = loadable(() => import(/* webpackChunkName: "OpsIdReviewPage" */ '../containers/OpsIdReviewPage/OpsIdReviewPage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ManageAccountPage',
  'AccountProfilePage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig, accessControlConfig) => {
  const isSearchPageWithMap = layoutConfig.searchPage?.variantType === 'map';
  const SearchPage = isSearchPageWithMap ? SearchPageWithMap : SearchPageWithGrid;
  const ListingPage = layoutConfig.listingPage?.variantType === 'carousel' 
    ? ListingPageCarousel 
    : ListingPageCoverPhoto;

  const isPrivateMarketplace = accessControlConfig?.marketplace?.private === true;
  const authForPrivateMarketplace = isPrivateMarketplace
    ? { auth: true, authPage: 'SignupPage' }
    : {};
  
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/dashboard',
      name: 'DashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: DashboardPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    {
      path: '/favorites',
      name: 'FavoritesPage',
      auth: true,
      authPage: 'LoginPage',
      component: FavoritesPage,
      loadData: pageDataLoadingAPI.FavoritesPage.loadData,
    },
    {
      path: '/past-sessions',
      name: 'PastSessionsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PastSessionsPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    {
      path: '/notifications',
      name: 'NotificationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: NotificationsPage,
    },
    {
      path: '/calendar',
      name: 'SessionsCalendarPage',
      auth: true,
      authPage: 'LoginPage',
      component: SessionsCalendarPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    {
      path: '/earnings',
      name: 'ListenerEarningsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ListenerEarningsPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    {
      path: '/availability',
      name: 'AvailabilityPage',
      auth: true,
      authPage: 'LoginPage',
      component: AvailabilityPage,
      loadData: pageDataLoadingAPI.DashboardPage.loadData,
    },
    // Dashboard / Inbox are dest-only. Do not add an authenticated catch-all
    // or LandingPage redirect here — signed-in users must still reach /, /s,
    // listings, become-a-listener, and account pages.
    {
      path: '/p/sweet-treat',
      name: 'SweetTreatPage',
      component: SweetTreatPage,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    // NOTE: when the private marketplace feature is enabled, the '/s' route is disallowed by the robots.txt resource.
    // If you add new routes that start with '/s*' (e.g. /support), you should add them to the robotsPrivateMarketplace.txt file.
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: BrowsePage, // Sam 6:44 PM Find Your Person (old: SearchPage)
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeLibraryLoading: {
        map: isSearchPageWithMap,
      },
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
      prioritizeLibraryLoading: {
        map: isSearchPageWithMap,
      },
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeLibraryLoading: {
        map: true,
      },
    },
    {
      path: '/l/:slug/:id/make-offer',
      name: 'MakeOfferPage',
      auth: true,
      authPage: 'SignupPage',
      component: MakeOfferPage,
      loadData: pageDataLoadingAPI.MakeOfferPage.loadData,
    },
    {
      path: '/l/:slug/:id/request-quote',
      name: 'RequestQuotePage',
      auth: true,
      authPage: 'SignupPage',
      component: RequestQuotePage,
      extraProps: { mode: 'request-quote' },
      loadData: pageDataLoadingAPI.RequestQuotePage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      authPage: 'SignupPage',
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
      prioritizeLibraryLoading: {
        stripe: true,
      },
    },
    {
      path: '/book/free',
      name: 'FreeBookingPage',
      // Sam Nov 3 12:53: guests see the booking page first; account/verify happens later in-flow.
      component: FreeBookingPage,
    },
    {
      path: '/l/:slug/:id/book',
      name: 'ListingBookingPage',
      ...authForPrivateMarketplace,
      component: ListingBookingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:id/book',
      name: 'ListingBookingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingBookingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeLibraryLoading: {
        map: true,
      },
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      authPage: 'LoginPage',
      component: NewListingPage,
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      authPage: 'LoginPage',
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
      prioritizeLibraryLoading: {
        stripe: true,
      },
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
      prioritizeLibraryLoading: {
        stripe: true,
      },
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      prioritizeLibraryLoading: {
        map: true,
      },
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile',
      name: 'YourProfilePage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
      loadData: pageDataLoadingAPI.ProfileSettingsPage.loadData,
    },
    {
      path: '/profile/:tab',
      name: 'YourProfilePageTab',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
      loadData: pageDataLoadingAPI.ProfileSettingsPage.loadData,
    },
    {
      path: '/my-profile',
      name: 'MyProfileHubPage',
      auth: true,
      authPage: 'LoginPage',
      component: MyProfileHubPage,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect name="YourProfilePage" search="?edit=talking" />
      ),
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/choose-role',
      name: 'ChooseAccountRolePage',
      auth: true,
      authPage: 'SignupPage',
      component: ChooseAccountRolePage,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxLanding,
    },
    {
      path: '/bookings',
      name: 'MyBookingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxLanding,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData(
          { ...params, transactionRole: 'provider' },
          ...rest
        ),
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
      prioritizeLibraryLoading: {
        stripe: true,
      },
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
      prioritizeLibraryLoading: {
        stripe: true,
      },
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
      prioritizeLibraryLoading: {
        stripe: true,
      },
    },
    {
      path: '/account/manage',
      name: 'ManageAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageAccountPage,
      loadData: pageDataLoadingAPI.ManageAccountPage.loadData,
    },
    {
      path: '/account/identity/return',
      name: 'IdentityReturnPage',
      auth: true,
      authPage: 'LoginPage',
      component: IdentityReturnPage,
      loadData: pageDataLoadingAPI.IdentityReturnPage.loadData,
    },
    {
      path: '/account/profile',
      name: 'AccountProfilePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ContactDetailsPage" />,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      path: '/how-it-works',
      name: 'HowItWorksPage',
      component: HowItWorksPage,
    },
    {
      path: '/for-talkers',
      name: 'ForTalkersPage',
      component: ForTalkersPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/talk',
      name: 'TalkRedirectPage',
      component: () => <NamedRedirect name="HowItWorksPage" />,
    },
    // Theme ad landings (Sam Oct 29): home model, issue header, topic-filtered people.
    ...TALK_THEME_IDS.map(id => ({
      path: `/talk/${id}`,
      name: `TalkTheme_${id}`,
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
      extraProps: { themeId: id },
    })),
    {
      path: '/talk/night-anxiety',
      name: 'TalkThemeNightAnxietyRedirect',
      component: () => <NamedRedirect name="LandingPage" />,
    },
    {
      path: '/talk/work-dread',
      name: 'TalkThemeWorkDreadRedirect',
      component: () => <NamedRedirect name="LandingPage" />,
    },
    {
      path: '/talk/meeting-dread',
      name: 'TalkThemeMeetingDreadRedirect',
      component: () => <NamedRedirect name="LandingPage" />,
    },
    {
      path: '/talk/cant-sleep',
      name: 'TalkThemeCantSleepRedirect',
      component: () => <NamedRedirect name="LandingPage" />,
    },
    {
      path: '/talk/work-career',
      name: 'TalkThemeWorkCareerRedirect',
      component: () => <NamedRedirect name="TalkTheme_work" />,
    },
    {
      path: '/talk/overthinking',
      name: 'TalkThemeOverthinkingRedirect',
      component: () => <NamedRedirect name="TalkTheme_anxiety" />,
    },
    {
      path: '/talk-and-listen',
      name: 'TalkAndListenPage',
      component: TalkAndListenPage,
    },
    {
      path: '/for-listeners',
      name: 'ForListenersPage',
      component: ForListenersPage,
    },
    {
      path: '/faq',
      name: 'FAQPage',
      component: FAQPage,
    },
    {
      path: '/safety',
      name: 'SafetyPage',
      component: SafetyPage,
    },
    {
      path: '/safety-and-security',
      name: 'SafetyAliasPage',
      component: SafetyPage,
    },
    {
      path: '/pricing',
      name: 'PricingPage',
      component: PricingPage,
    },
    {
      path: '/start',
      name: 'StartAdPage',
      component: StartAdPage,
    },
    {
      path: '/become-a-listener',
      name: 'BecomeAListenerPage',
      component: BecomeAListenerPage,
    },
    {
      path: '/help',
      name: 'HelpPage',
      component: ContentPage,
      extraProps: { pageId: 'help' },
    },
    {
      path: '/support',
      name: 'SupportPage',
      component: () => <NamedRedirect name="HelpPage" />,
    },
    {
      path: '/promote',
      name: 'PromotePage',
      component: PromotePage,
    },
    {
      path: '/share',
      name: 'SharePage',
      component: () => <NamedRedirect name="PromotePage" />,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
    },
    {
      path: '/earners-playbook',
      name: 'EarnersPlaybookPage',
      component: EarnersPlaybookPage,
    },
    {
      path: '/tips',
      name: 'TipsPage',
      component: TipsPage,
    },
    {
      path: '/pay-someone-to-listen',
      name: 'PaySomeoneToListenPage',
      component: TopicPage,
      extraProps: { topicId: 'pay-someone-to-listen' },
    },
    // Talker Q&A landing pages (Oct 21 2026), incl. /someone-to-talk-to and /someone-to-talk-to-at-night.
    ...TALKER_QA_PAGES.map(p => ({
      path: p.path,
      name: p.name,
      component: TalkerQAPage,
      extraProps: { qaId: p.id },
    })),
    {
      path: '/lonely',
      name: 'LonelyPage',
      component: TopicPage,
      extraProps: { topicId: 'lonely' },
    },
    {
      path: '/topics',
      name: 'TopicsPage',
      component: TopicPage,
      extraProps: { topicId: 'topics' },
    },
    // Sweet Skills early access (standalone; full mentor browse lives in containers/SweetSkillsPage, unrouted)
    { path: '/skills', name: 'SweetSkillsPage', component: SweetSkillsPage },
    { path: '/learn', name: 'LearnPage', component: LearnPage, loadData: pageDataLoadingAPI.LandingPage.loadData },
    { path: '/teach', name: 'TeachPage', component: TeachPage },
    { path: '/become-an-earner', name: 'BecomeAnEarnerPage', component: BecomeAnEarnerPage },
    { path: '/free-15', name: 'Free15Page', component: Free15Page },
    { path: '/talk-free', name: 'GuestTalkPage', component: GuestTalkPage },
    { path: '/welcome', name: 'WelcomePage', component: WelcomePage, auth: true, authPage: 'SignupPage' },
    // Oct 22 merge: one Browse page. /skills/browse is a filtered view of /s (server 301 + client fallback).
    { path: '/skills/browse', name: 'SweetSkillsBrowsePage', component: SweetSkillsBrowsePage },
    { path: '/talk-about/:topicSlug', name: 'TalkAboutTopicPage', component: TalkAboutTopicPage },
    // Hidden SEO topic pages (sitemap/Google only — not linked from nav/footer).
    ...TOPIC_PAGE_ROUTES.filter(r => !TALKER_QA_PAGES.some(p => p.path === r.path)).map(r => ({
      path: r.path,
      name: r.name,
      component: TopicPage,
      extraProps: { topicId: r.id },
    })),
    {
      path: '/guides',
      name: 'GuidesPage',
      component: GuidePage,
    },
    {
      path: '/guides/:guideId',
      name: 'GuidePage',
      component: GuidePage,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/terms',
      name: 'TermsOfServiceAliasPage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/sms-terms',
      name: 'SmsTermsPage',
      component: SmsTermsPage,
    },
    {
      path: '/sms',
      name: 'SmsTermsAliasPage',
      component: SmsTermsPage,
    },
    {
      path: '/privacy',
      name: 'PrivacyPolicyAliasPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/ops/moderation',
      name: 'OpsModerationPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsModerationPage,
    },
    {
      path: '/ops/safety',
      name: 'SafetyReviewPage',
      auth: true,
      authPage: 'LoginPage',
      component: SafetyReviewPage,
    },
    {
      path: '/ops/founding',
      name: 'OpsFoundingPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsFoundingPage,
    },
    {
      path: '/ops/promotions',
      name: 'OpsPromotionsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsPromotionsPage,
    },
    {
      path: '/ops/hello-video',
      name: 'OpsHelloVideoPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsHelloVideoPage,
    },
    {
      path: '/ops/id-review',
      name: 'OpsIdReviewPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsIdReviewPage,
    },
    {
      path: '/ops/paypal-payouts',
      name: 'OpsPaypalPayoutsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsPaypalPayoutsPage,
    },
    {
      path: '/ops/insights',
      name: 'OpsInsightsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OpsInsightsPage,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      auth: true,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      auth: true,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      auth: true,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      auth: true,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      auth: true,
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      // Strict onboarding: the first unfinished step + progress (Sam, Oct 7 2026).
      path: '/onboarding',
      name: 'OnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: OnboardingPage,
    },
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      // Not auth-gated: signed-out tap saves the token, then Log in applies it.
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
  ];
};

export default routeConfiguration;
